import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { CheckCircle2, CreditCard, Link2, PlugZap, RefreshCw, Mail } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";

const FEATURES = {
  starter_monthly: ["Up to 3 techs", "Dispatch + offline PWA", "Estimates & signatures", "Card & ACH payments"],
  fleet_monthly: ["Everything in Starter, plus:", "Up to 10 techs", "Voice debrief AI review", "Fleet, tools & inventory", "QuickBooks Online & Xero sync", "Pricebook & branded quotes"],
  enterprise_monthly: ["Everything in Fleet, plus:", "Unlimited techs", "Subcontractor controls", "Advanced reconciliation", "Priority SLA", "Full access to every module"],
};

function QboCard() {
  const [qbo, setQbo] = useState(null);
  const load = () => api.get("/qbo/status").then((r) => setQbo(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("qbo") === "connected") toast.success("QuickBooks connected");
  }, []);
  const connect = async () => {
    try { const { data } = await api.get("/qbo/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const reconcile = async () => {
    try { await api.post("/qbo/reconcile"); toast.success("Ledger reconciled"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/qbo/disconnect"); toast.success("Disconnected"); load(); };
  if (!qbo) return null;
  return (
    <div className="mt-10 card-tac p-6" data-testid="qbo-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/10 text-emerald-400"><PlugZap className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold text-white">QuickBooks Online</h3>
            <p className="font-mono-tac text-xs text-slate-500">Two-way invoice & PO sync · {qbo.env}</p>
          </div>
        </div>
        <StatusPill status={qbo.connected ? "active" : "none"} />
      </div>
      {!qbo.configured ? (
        <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="qbo-not-configured">
          Add your Intuit developer <span className="font-mono-tac">CLIENT_ID</span> & <span className="font-mono-tac">CLIENT_SECRET</span> to activate the live connection. Redirect URI to register: <span className="font-mono-tac break-all">/api/qbo/callback</span>
        </p>
      ) : qbo.connected ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="qbo-reconcile-btn" onClick={reconcile} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-white hover:border-cyan-500"><RefreshCw className="h-4 w-4" /> Reconcile ledger</button>
          <button data-testid="qbo-disconnect-btn" onClick={disconnect} className="rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 hover:border-rose-500">Disconnect</button>
          {qbo.last_sync && <span className="self-center font-mono-tac text-xs text-slate-500">last sync {qbo.last_sync.slice(0, 19)}</span>}
        </div>
      ) : (
        <button data-testid="qbo-connect-btn" onClick={connect} className="btn-amber mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm"><Link2 className="h-4 w-4" /> Connect QuickBooks</button>
      )}
    </div>
  );
}

function XeroCard() {
  const [xero, setXero] = useState(null);
  const [tenants, setTenants] = useState([]);
  const load = () => api.get("/xero/status").then((r) => setXero(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("xero");
    if (p === "connected") toast.success("Xero connected");
    if (p === "error") toast.error("Xero connection failed");
    if (p === "choose") api.get("/xero/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, []);
  const connect = async () => {
    try { const { data } = await api.get("/xero/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const choose = async (tenant_id) => {
    try { await api.post("/xero/tenants/select", { tenant_id }); toast.success("Xero organisation linked"); setTenants([]); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/xero/disconnect"); toast.success("Disconnected"); load(); };
  if (!xero) return null;
  return (
    <div className="mt-6 card-tac p-6" data-testid="xero-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-sky-500/10 text-sky-400"><PlugZap className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold text-white">Xero</h3>
            <p className="font-mono-tac text-xs text-slate-500">Push accepted quotes as invoices{xero.tenant_name ? ` · ${xero.tenant_name}` : ""}</p>
          </div>
        </div>
        <StatusPill status={xero.connected ? "active" : "none"} />
      </div>
      {!xero.configured ? (
        <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="xero-not-configured">
          Add your Xero <span className="font-mono-tac">CLIENT_ID</span> & <span className="font-mono-tac">CLIENT_SECRET</span>. Redirect URI to register: <span className="font-mono-tac break-all">/api/xero/callback</span>
        </p>
      ) : tenants.length > 0 ? (
        <div className="mt-4 space-y-2" data-testid="xero-tenant-choose">
          <p className="text-sm text-slate-400">Choose the organisation to link:</p>
          {tenants.map((t) => (
            <button key={t.tenant_id} onClick={() => choose(t.tenant_id)} className="block w-full rounded-lg border border-slate-700 px-4 py-2 text-left text-sm text-white hover:border-sky-500">{t.tenant_name || t.tenant_id}</button>
          ))}
        </div>
      ) : xero.connected ? (
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="xero-disconnect-btn" onClick={disconnect} className="rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 hover:border-rose-500">Disconnect</button>
          {xero.last_sync && <span className="self-center font-mono-tac text-xs text-slate-500">last sync {xero.last_sync.slice(0, 19)}</span>}
        </div>
      ) : (
        <button data-testid="xero-connect-btn" onClick={connect} className="btn-amber mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm"><Link2 className="h-4 w-4" /> Connect Xero</button>
      )}
    </div>
  );
}

function GmailCard() {
  const [gmail, setGmail] = useState(null);
  const load = () => api.get("/oauth/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (p === "connected") toast.success("Gmail connected");
    if (p === "error") toast.error("Gmail connection failed");
  }, []);
  const connect = async () => {
    try { const { data } = await api.get("/oauth/gmail/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/oauth/gmail/disconnect"); toast.success("Disconnected"); load(); };
  if (!gmail) return null;
  return (
    <div className="mt-6 card-tac p-6" data-testid="gmail-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-rose-500/10 text-rose-400"><Mail className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold text-white">Gmail</h3>
            <p className="font-mono-tac text-xs text-slate-500">Send quote emails to customers{gmail.email ? ` · ${gmail.email}` : ""}</p>
          </div>
        </div>
        <StatusPill status={gmail.connected ? "active" : "none"} />
      </div>
      {!gmail.configured ? (
        <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="gmail-not-configured">
          Add your Google <span className="font-mono-tac">CLIENT_ID</span> & <span className="font-mono-tac">CLIENT_SECRET</span>. Redirect URI to register: <span className="font-mono-tac break-all">/api/oauth/gmail/callback</span>
        </p>
      ) : gmail.connected ? (
        <button data-testid="gmail-disconnect-btn" onClick={disconnect} className="mt-4 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 hover:border-rose-500">Disconnect</button>
      ) : (
        <button data-testid="gmail-connect-btn" onClick={connect} className="btn-amber mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm"><Link2 className="h-4 w-4" /> Connect Gmail</button>
      )}
    </div>
  );
}

export default function Billing() {
  const { workspace, refreshMe } = useAuth();
  const { data: plans, isLoading } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/billing/plans")).data });

  const subscribe = async (lookup_key) => {
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const testCharge = async () => {
    try {
      const { data } = await api.post("/billing/test-charge", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Eyebrow>Subscription</Eyebrow>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-bold text-white">Billing</h1>
        <StatusPill status={workspace?.subscription_status} />
      </div>
      <p className="mt-2 text-slate-400">Current plan: <span className="text-white">{plans[workspace?.plan]?.name || "Trial"}</span>. Upgrade or change anytime.</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {Object.entries(plans).map(([key, p]) => {
          const current = workspace?.plan === key && workspace?.subscription_status === "active";
          return (
            <div key={key} data-testid={`billing-plan-${key}`} className={`card-tac flex flex-col p-8 ${key === "fleet_monthly" ? "ring-2 ring-amber-500" : ""}`}>
              <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1"><span className="font-display text-4xl font-black text-white">${p.price}</span><span className="mb-1 text-sm text-slate-500">/mo</span></div>
              <ul className="mt-6 flex-1 space-y-3">
                {FEATURES[key].map((f) => f.startsWith("Everything in")
                  ? <li key={f} className="border-b border-slate-800 pb-2 text-sm font-semibold text-white">{f}</li>
                  : <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}</li>)}
              </ul>
              <button data-testid={`subscribe-btn-${key}`} disabled={current} onClick={() => subscribe(key)}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold ${current ? "border border-emerald-500/40 text-emerald-300" : "btn-amber"}`}>
                {current ? "Current plan" : <><CreditCard className="h-4 w-4" /> Subscribe</>}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-6 font-mono-tac text-xs text-slate-600">Live payments via Stripe. Stripe manages tax &amp; compliance for your subscription.</p>
      <div className="mt-3"><button data-testid="test-charge-btn" onClick={testCharge} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:border-emerald-500"><CreditCard className="h-4 w-4 text-emerald-400" /> Run $1 live test charge</button></div>
      <QboCard />
      <XeroCard />
      <GmailCard />
    </div>
  );
}
